import { useState, useEffect } from "react";
import { useBranding } from "@/contexts/BrandingContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const Input = (props) => (
  <input {...props}
    className={
      "w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm " +
      "focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none " + (props.className || "")
    } />
);
const Field = ({ label, children, hint }) => (
  <div>
    <label className="text-label block mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);
const ColorPicker = ({ value, onChange, testId, placeholder }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
      className="w-10 h-9 border border-border rounded-sm cursor-pointer bg-transparent"
      data-testid={`${testId}-picker`} />
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} data-testid={testId} />
  </div>
);

export default function Branding() {
  const { branding, save: saveBranding } = useBranding();
  const [form, setForm] = useState(branding);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setForm(branding); }, [branding]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value }));

  const save = async () => {
    setLoading(true);
    try {
      await saveBranding(form);
      toast.success("Branding updated globally");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-4xl" data-testid="branding-page">
      <header>
        <p className="text-label">Customize</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Branding</h1>
        <p className="text-sm text-muted-foreground mt-1">Changes apply globally for every user.</p>
      </header>

      <section className="border border-border rounded-sm bg-background p-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-4">Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Company Name">
              <Input value={form.company_name || ""} onChange={(e) => set("company_name")(e.target.value)}
                data-testid="branding-company-input" />
            </Field>
            <Field label="Company Website">
              <Input value={form.website || ""} onChange={(e) => set("website")(e.target.value)}
                placeholder="https://example.com" data-testid="branding-website" />
            </Field>
            <Field label="Logo URL" hint="Upload to any host (S3/Cloudinary), paste link here.">
              <Input value={form.logo_url || ""} onChange={(e) => set("logo_url")(e.target.value)} placeholder="https://…"
                data-testid="branding-logo-url" />
            </Field>
            <Field label="Favicon URL">
              <Input value={form.favicon_url || ""} onChange={(e) => set("favicon_url")(e.target.value)} placeholder="https://…"
                data-testid="branding-favicon-url" />
            </Field>
            <Field label="Login Screen Logo">
              <Input value={form.login_logo_url || ""} onChange={(e) => set("login_logo_url")(e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Login Background URL">
              <Input value={form.login_bg_url || ""} onChange={(e) => set("login_bg_url")(e.target.value)} placeholder="https://…" />
            </Field>
          </div>
        </div>

        <div className="pt-5 border-t border-border">
          <h3 className="text-sm font-semibold mb-4">Theme</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Primary Theme Color" hint="Used for buttons, accents, links.">
              <ColorPicker value={form.theme_color} onChange={set("theme_color")}
                testId="branding-color-input" placeholder="#0F172A" />
            </Field>
            <Field label="Sidebar Color">
              <ColorPicker value={form.sidebar_color} onChange={set("sidebar_color")}
                testId="branding-sidebar-color" placeholder="optional" />
            </Field>
            <Field label="Header Color">
              <ColorPicker value={form.header_color} onChange={set("header_color")}
                testId="branding-header-color" placeholder="optional" />
            </Field>
          </div>
        </div>

        <div className="pt-5 border-t border-border">
          <h3 className="text-sm font-semibold mb-4">Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => set("email")(e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => set("phone")(e.target.value)} /></Field>
            <Field label="Address"><Input value={form.address || ""} onChange={(e) => set("address")(e.target.value)} /></Field>
            <Field label="Copyright">
              <Input value={form.copyright || ""} onChange={(e) => set("copyright")(e.target.value)} placeholder="© 2026 Your Company" />
            </Field>
            <Field label="Footer Text" hint="Shown on login screen.">
              <Input value={form.footer_text || ""} onChange={(e) => set("footer_text")(e.target.value)} placeholder="Built securely for internal teams" />
            </Field>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-border">
          <button onClick={save} disabled={loading} data-testid="save-branding-btn"
            className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: "var(--theme-color)" }}>
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </section>
    </div>
  );
}
